/**
 * Who creates enquiries from mail automatically — the browser, or the Waves proxy?
 *
 * Since 2026-09-21 the proxy can run the pipeline itself (`BOSUN_AUTO_ENQUIRY=1`, see
 * `server/mailWatcher.entry.ts`), with nobody logged in. It says so on `/healthz`
 * (`autoEnquiry: 'running'`), and the browser's runner (`useAutoEnquiries`) stands down when
 * it does: both re-check the server before creating, but two creators polling the same inbox
 * would still both find a mail before either's enquiry exists.
 *
 * In dev there is no proxy — `/healthz` is the SPA fallback, not JSON — which reads as "the
 * browser runs", exactly as before.
 */
import { useQuery } from '@tanstack/react-query'

export function serverOwnsAutoEnquiries(health: unknown): boolean {
  if (!health || typeof health !== 'object') return false
  const state = (health as { autoEnquiry?: unknown }).autoEnquiry
  return state === 'running' || state === 'starting'
}

async function readHealth(): Promise<unknown> {
  const response = await fetch('/healthz', { credentials: 'same-origin' })
  if (!response.ok || !(response.headers.get('content-type') ?? '').includes('application/json')) return null
  return response.json()
}

/** `true` while the proxy's watcher owns automatic enquiries; `false` until known. */
export function useServerAutoEnquiry(): boolean {
  const health = useQuery({ queryKey: ['healthz'], queryFn: readHealth, staleTime: 5 * 60_000, retry: false })
  return serverOwnsAutoEnquiries(health.data)
}
